import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Brain, Pin, PinOff, Trash2, Edit2, Plus, Search, ToggleLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const KIND_COLORS = {
  personal_preference: 'bg-violet-500/10 text-violet-600 border-violet-500/30',
  org_knowledge:       'bg-cyan-500/10 text-cyan-600 border-cyan-500/30',
  module_knowledge:    'bg-blue-500/10 text-blue-600 border-blue-500/30',
  learned_rule:        'bg-amber-500/10 text-amber-600 border-amber-500/30',
  task_classification: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  switchboard_triage:  'bg-fuchsia-500/10 text-fuchsia-600 border-fuchsia-500/30',
  conversation_fact:   'bg-slate-500/10 text-slate-600 border-slate-500/30',
  external_source:     'bg-rose-500/10 text-rose-600 border-rose-500/30',
};

const fmtTime = (s) => {
  if (!s) return '';
  try { return new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
};

export default function MemoryV2Panel() {
  const [taxonomy, setTaxonomy] = useState([]);
  const [entries, setEntries] = useState([]);
  const [filterKind, setFilterKind] = useState('');
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // entry being edited
  const [draft, setDraft] = useState({ kind: 'personal_preference', summary: '', detail: '', pinned: false });

  const fetchTaxonomy = useCallback(async () => {
    const res = await fetch('/api/ai/v2/memory/taxonomy', { credentials: 'include' });
    if (res.ok) {
      const d = await res.json();
      setTaxonomy(d.kinds || []);
    }
  }, []);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const url = filterKind ? `/api/ai/v2/memory?kind=${encodeURIComponent(filterKind)}&limit=200` : '/api/ai/v2/memory?limit=200';
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setEntries(d.entries || []);
      }
    } finally { setLoading(false); }
  }, [filterKind]);

  useEffect(() => { fetchTaxonomy(); }, [fetchTaxonomy]);
  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const runSearch = async () => {
    if (!search.trim()) { setSearchResults(null); return; }
    const res = await fetch('/api/ai/v2/memory/search', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ query: search, limit: 20, kind: filterKind || undefined }),
    });
    if (res.ok) {
      const d = await res.json();
      setSearchResults(d.entries || []);
    } else {
      toast.error('Search failed');
    }
  };

  const toggleKind = async (kind, enabled) => {
    const res = await fetch(`/api/ai/v2/memory/kinds/${kind}/toggle`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ enabled }),
    });
    if (res.ok) {
      fetchTaxonomy();
      toast.success(enabled ? `Enabled "${kind}"` : `Disabled "${kind}"`);
    }
  };

  const togglePinned = async (entry) => {
    const res = await fetch(`/api/ai/v2/memory/${entry.memory_id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ pinned: !entry.pinned }),
    });
    if (res.ok) fetchEntries();
  };

  const toggleEnabled = async (entry) => {
    const res = await fetch(`/api/ai/v2/memory/${entry.memory_id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ enabled: !entry.enabled }),
    });
    if (res.ok) fetchEntries();
  };

  const removeEntry = async (entry) => {
    if (!confirm(`Delete this memory? This cannot be undone.\n\n${entry.summary}`)) return;
    const res = await fetch(`/api/ai/v2/memory/${entry.memory_id}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (res.ok) {
      toast.success('Memory deleted');
      fetchEntries();
    }
  };

  const submitDraft = async () => {
    if (!draft.summary.trim()) { toast.error('Summary required'); return; }
    const url = editing ? `/api/ai/v2/memory/${editing.memory_id}` : '/api/ai/v2/memory';
    const method = editing ? 'PUT' : 'POST';
    const body = editing
      ? { summary: draft.summary, detail: draft.detail, pinned: draft.pinned }
      : { kind: draft.kind, summary: draft.summary, detail: draft.detail, pinned: draft.pinned };
    const res = await fetch(url, {
      method, headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify(body),
    });
    if (res.ok) {
      toast.success(editing ? 'Memory updated' : 'Memory added');
      setCreating(false); setEditing(null);
      setDraft({ kind: 'personal_preference', summary: '', detail: '', pinned: false });
      fetchEntries();
    } else {
      const d = await res.json().catch(() => ({}));
      toast.error(d?.detail?.message || 'Save failed');
    }
  };

  const openEdit = (entry) => {
    setEditing(entry);
    setDraft({ kind: entry.kind, summary: entry.summary, detail: entry.detail || '', pinned: !!entry.pinned });
    setCreating(true);
  };

  const visible = searchResults ?? entries;

  return (
    <Card className="h-full overflow-hidden flex flex-col" data-testid="memory-v2-panel">
      <CardHeader className="py-4 px-6 border-b">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Brain className="w-5 h-5 text-violet-500" />Operational Memory</CardTitle>
            <CardDescription>Editable rules, preferences and learned classifications the AI applies every turn</CardDescription>
          </div>
          <Button size="sm" onClick={() => { setEditing(null); setDraft({ kind: 'personal_preference', summary: '', detail: '', pinned: false }); setCreating(true); }} data-testid="memory-v2-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add memory
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-0 flex-1 overflow-hidden">
        <div className="grid grid-cols-3 h-full">
          {/* Left: kinds + toggles */}
          <div className="border-r p-4 overflow-y-auto" data-testid="memory-v2-kinds">
            <h4 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Categories</h4>
            <div className="space-y-1">
              <button
                className={cn('w-full text-left px-2 py-1.5 rounded text-sm hover:bg-accent transition-colors',
                  filterKind === '' && 'bg-accent font-medium')}
                onClick={() => { setFilterKind(''); setSearchResults(null); }}>
                All ({entries.length})
              </button>
              {taxonomy.map(k => (
                <div key={k.kind} className="flex items-center justify-between gap-2 group">
                  <button
                    className={cn('flex-1 text-left px-2 py-1.5 rounded text-sm hover:bg-accent transition-colors',
                      filterKind === k.kind && 'bg-accent font-medium')}
                    onClick={() => { setFilterKind(k.kind); setSearchResults(null); }}
                    data-testid={`memory-v2-kind-${k.kind}`}>
                    <span className={cn('inline-block w-2 h-2 rounded-full mr-2', KIND_COLORS[k.kind]?.split(' ')[0] || 'bg-slate-500')} />
                    {k.label}
                  </button>
                  <Switch
                    checked={k.enabled}
                    onCheckedChange={(v) => toggleKind(k.kind, v)}
                    data-testid={`memory-v2-toggle-${k.kind}`}
                    className="scale-75" />
                </div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-3 leading-snug">
              Disabled categories are NOT injected into AI prompts. Toggling here is global per category.
            </p>
          </div>

          {/* Right: entries + search */}
          <div className="col-span-2 flex flex-col overflow-hidden">
            <div className="p-3 border-b flex gap-2">
              <Input
                placeholder="Search memories…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && runSearch()}
                data-testid="memory-v2-search-input" />
              <Button onClick={runSearch} variant="outline" size="sm"><Search className="w-4 h-4" /></Button>
              {searchResults && (
                <Button onClick={() => { setSearch(''); setSearchResults(null); }} variant="ghost" size="sm">Clear</Button>
              )}
            </div>

            <ScrollArea className="flex-1">
              {loading ? (
                <div className="p-8 flex items-center justify-center text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : visible.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  {searchResults ? 'No matching memories.' : 'No memories yet — corrections you make to AI-created tasks land here automatically.'}
                </div>
              ) : (
                <div className="divide-y">
                  {visible.map(e => (
                    <div key={e.memory_id} className={cn('p-3 hover:bg-accent/30 transition', !e.enabled && 'opacity-50')} data-testid={`memory-v2-entry-${e.memory_id}`}>
                      <div className="flex items-start gap-2">
                        <Badge variant="outline" className={cn('text-[10px]', KIND_COLORS[e.kind])}>
                          {e.kind_label || e.kind}
                        </Badge>
                        {e.pinned && <Badge variant="secondary" className="text-[10px]">pinned</Badge>}
                        {(e.provenance?.source === 'task_correction' || e.provenance?.source === 'user_correction') && (
                          <Badge variant="secondary" className="text-[10px]">auto-learned</Badge>
                        )}
                        <div className="flex-1" />
                        <Button size="icon" variant="ghost" className="h-6 w-6" title={e.pinned ? 'Unpin' : 'Pin'} onClick={() => togglePinned(e)}>
                          {e.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                        </Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6" title="Edit" onClick={() => openEdit(e)}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6" title={e.enabled ? 'Disable' : 'Enable'} onClick={() => toggleEnabled(e)}>
                          <ToggleLeft className={cn('w-3.5 h-3.5', !e.enabled && 'rotate-180')} />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" title="Delete" onClick={() => removeEntry(e)} data-testid={`memory-v2-delete-${e.memory_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <p className="text-sm mt-1">{e.summary}</p>
                      {e.detail && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{e.detail}</p>}
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                        <span>{fmtTime(e.updated_at || e.created_at)}</span>
                        {e.use_count > 0 && <span>· used {e.use_count}×</span>}
                        {e.provenance?.source_ref && <span>· ref {String(e.provenance.source_ref).slice(0, 16)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      </CardContent>

      {/* Create / edit dialog */}
      <Dialog open={creating} onOpenChange={(v) => { if (!v) { setCreating(false); setEditing(null); } }}>
        <DialogContent data-testid="memory-v2-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit memory' : 'Add memory'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!editing && (
              <div>
                <label className="text-xs text-muted-foreground">Category</label>
                <Select value={draft.kind} onValueChange={(v) => setDraft({ ...draft, kind: v })}>
                  <SelectTrigger data-testid="memory-v2-dialog-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {taxonomy.map(k => <SelectItem key={k.kind} value={k.kind}>{k.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-xs text-muted-foreground">Summary (1 line, applied directly by AI)</label>
              <Input value={draft.summary} onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
                placeholder='e.g. "Always schedule client meetings as 30min, not 60min"'
                data-testid="memory-v2-dialog-summary" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Detail (optional)</label>
              <Textarea value={draft.detail} onChange={(e) => setDraft({ ...draft, detail: e.target.value })}
                placeholder="Long-form context the AI can read in detail."
                className="min-h-24" data-testid="memory-v2-dialog-detail" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={draft.pinned} onCheckedChange={(v) => setDraft({ ...draft, pinned: v })} data-testid="memory-v2-dialog-pinned" />
              <label className="text-sm">Pin (always inject into prompt)</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreating(false); setEditing(null); }}>Cancel</Button>
            <Button onClick={submitDraft} data-testid="memory-v2-dialog-save">{editing ? 'Save' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
